import React, { useState, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { DatePicker, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToExportReports,
  reqToGetAnalytics,
} from "../../redux/slices.js/discountmanagement/discountPromotionSlice";
import * as XLSX from "xlsx";

const items = ["Program", "Analytics", "Code", "Applications"];
const billingCycles = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Annually", value: "annually" },
];

const { RangePicker } = DatePicker;

const AnalyticsTabs = ({ totalCount, totalPages, setCurrentPage }) => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState("monthly");
  const [selectedItems, setSelectedItems] = useState({
    Program: false,
    Analytics: false,
    Code: false,
    Applications: false,
  });
  const [exportLoading, setExportLoading] = useState(false);
  const analytics = useSelector(
    (state) => state?.discountCoupons?.analytics_data || []
  );

  useEffect(() => {
    dispatch(
      reqToGetAnalytics({
        data: {},
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 120);
    return () => clearTimeout(t);
  }, []);

  const handleItemChange = (item) => {
    setSelectedItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  const handleCycleChange = (event) => {
    setSelectedCycle(event.target.value);
  };

  const handleGenerateReport = () => {
    if (!selectedDate || selectedDate.length !== 2) {
      message.error("Please select a date range");
      return;
    }

    if (!Object.values(selectedItems).some((value) => value)) {
      message.error("Please select at least one report type");
      return;
    }

    const payload = {
      start_date: selectedDate[0].format("YYYY-MM-DD"),
      end_date: selectedDate[1].format("YYYY-MM-DD"),
      program: selectedItems.Program,
      code: selectedItems.Code,
      analytic: selectedItems.Analytics,
      application: selectedItems.Applications,
      formate: selectedCycle,
    };

    setExportLoading(true);

    dispatch(
      reqToExportReports({
        data: payload,
        onSuccess: (response) => {
          setExportLoading(false);

          // Check if we have any data to export
          if (!response?.result) {
            message.warning("No data available for export");
            return;
          }

          // Create a new workbook
          const workbook = XLSX.utils.book_new();

          // Helper function to add sheet to workbook
          const addSheetToWorkbook = (data, headers, sheetName) => {
            if (!data || data.length === 0) return;

            // Create worksheet data with headers as first row
            const worksheetData = [
              headers,
              ...data.map((row) => headers.map((header) => row[header] || "")),
            ];

            // Create worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
          };

          // Process Program data if selected and available
          if (selectedItems.Program && response.result.program) {
            const programData = response.result.program;
            if (Array.isArray(programData) && programData.length > 0) {
              const headers = [
                "ID",
                "Program Name",
                "Discount Type",
                "Discount Value",
                "Duration",
                "Eligibility Rules",
                "Max Redemptions",
                "Approval Threshold",
                "Status",
                "Created Date",
                "User Name",
              ];

              const rows = programData.map((program) => ({
                ID: program.id,
                "Program Name": program.name,
                "Discount Type":
                  program.discount_type === 1 ? "Fixed" : "Percentage",
                "Discount Value": program.discount_value,
                Duration: program.duration,
                "Eligibility Rules": Array.isArray(program.eligibility_rules)
                  ? program.eligibility_rules.join(", ")
                  : "",
                "Max Redemptions": program.max_redemptions,
                "Approval Threshold": program.approval_threshold,
                Status: program.status,
                "Created Date": program.createdAt,
                "User Name": program.user?.name || "",
              }));

              addSheetToWorkbook(rows, headers, "Programs");
            } else {
              // Add empty sheet if no data but selected
              const emptyWorksheet = XLSX.utils.aoa_to_sheet([
                ["No data available"],
              ]);
              XLSX.utils.book_append_sheet(
                workbook,
                emptyWorksheet,
                "Programs"
              );
            }
          }

          // Process Code data if selected and available
          if (selectedItems.Code && response.result.code) {
            const codeData = response.result.code;
            if (Array.isArray(codeData) && codeData.length > 0) {
              const headers = [
                "ID",
                "Channel",
                "Created Date",
                "Expires At",
                "Name",
                "Number of Codes",
                "Program",
                "Start Date",
                "Uses Per Code",
                "Status",
                "User",
              ];

              const rows = codeData.map((r) => ({
                ID: r.id,
                Channel: r.channel,
                "Created Date": r.createdAt,
                "Expires At": r.expires_at || "",
                Name: r.name || "",
                "Number of Codes": r.number_of_code || "",
                Program: r.program?.name || "",
                "Start Date": r.start_date || "",
                "Uses Per Code": r.uses_per_code || "",
                Status: r.status || "",
                User: r.user?.name || "",
              }));

              addSheetToWorkbook(rows, headers, "Codes");
            } else {
              const emptyWorksheet = XLSX.utils.aoa_to_sheet([
                ["No data available"],
              ]);
              XLSX.utils.book_append_sheet(workbook, emptyWorksheet, "Codes");
            }
          }

          // Process Analytics data if selected and available
          if (selectedItems.Analytics && response.result.analytic) {
            const analyticData = response.result.analytic;

            // Create one comprehensive Analytics sheet instead of multiple sheets
            const analyticsHeaders = ["Metric", "Value"];
            const analyticsRows = [];

            // Add top dashboard data
            if (analyticData.topDashbordData) {
              analyticsRows.push(
                {
                  Metric: "Revenue Protected",
                  Value: analyticData.topDashbordData.revenue_protected || 0,
                },
                { Metric: "MRR", Value: analyticData.topDashbordData.mrr || 0 },
                {
                  Metric: "Active Programs",
                  Value: analyticData.topDashbordData.activeProgram || 0,
                },
                {
                  Metric: "Total Redemptions",
                  Value: analyticData.topDashbordData.total_redemptions || 0,
                },
                {
                  Metric: "Conversion Lift",
                  Value: `${
                    analyticData.topDashbordData.conversion_lift || 0
                  }%`,
                }
              );
            }

            // Add revenue chart summary
            if (analyticData.revenueChart && analyticData.revenueChart.series) {
              const totalRevenue = analyticData.revenueChart.series.reduce(
                (sum, val) => sum + (val || 0),
                0
              );
              const maxRevenue = Math.max(...analyticData.revenueChart.series);
              analyticsRows.push(
                { Metric: "Total Revenue (Period)", Value: totalRevenue },
                { Metric: "Peak Revenue Month", Value: maxRevenue }
              );
            }

            // Add channel usage summary
            if (
              analyticData.channelChart &&
              analyticData.channelChart.series?.data
            ) {
              const totalUsage = analyticData.channelChart.series.data.reduce(
                (sum, val) => sum + (val || 0),
                0
              );
              const maxChannel = Math.max(
                ...analyticData.channelChart.series.data
              );
              analyticsRows.push(
                { Metric: "Total Channel Usage", Value: totalUsage },
                { Metric: "Highest Channel Usage", Value: maxChannel }
              );
            }

            if (analyticsRows.length > 0) {
              addSheetToWorkbook(analyticsRows, analyticsHeaders, "Analytics");
            } else {
              const emptyWorksheet = XLSX.utils.aoa_to_sheet([
                ["No analytics data available"],
              ]);
              XLSX.utils.book_append_sheet(
                workbook,
                emptyWorksheet,
                "Analytics"
              );
            }
          }

          // Process Application data if selected and available
          if (selectedItems.Applications && response.result.application) {
            const applicationData = response.result.application;
            if (
              applicationData.rows &&
              Array.isArray(applicationData.rows) &&
              applicationData.rows.length > 0
            ) {
              const headers = [
                "ID",
                "Name",
                "Program Name",
                "Discount Value",
                "Channel",
                "Status",
                "Total Codes",
                "Used Codes",
                "MRR Impact",
                "Start Date",
                "Expires At",
                "User Name",
              ];

              const rows = applicationData.rows.map((app) => ({
                ID: app.id,
                Name: app.name,
                "Program Name": app.program?.name || "",
                "Discount Value": app.program?.discount_value || "",
                Channel: app.channel,
                Status: app.status,
                "Total Codes": app.total_codes || 0,
                "Used Codes": app.used_codes || 0,
                "MRR Impact": app.mrr_impact || 0,
                "Start Date": app.start_date || "",
                "Expires At": app.expires_at || "",
                "User Name": app.user?.name || "",
              }));

              addSheetToWorkbook(rows, headers, "Applications");
            } else {
              const emptyWorksheet = XLSX.utils.aoa_to_sheet([
                ["No data available"],
              ]);
              XLSX.utils.book_append_sheet(
                workbook,
                emptyWorksheet,
                "Applications"
              );
            }
          }

          // Check if we have any sheets in the workbook
          if (workbook.SheetNames.length === 0) {
            message.warning("No data available for the selected report types");
            return;
          }

          // Generate Excel file and download
          try {
            const date = new Date().toISOString().slice(0, 10);
            const filename = `reports_export_${date}.xlsx`;

            // Write workbook to file and trigger download
            XLSX.writeFile(workbook, filename);

            const selectedCount =
              Object.values(selectedItems).filter(Boolean).length;
            message.success(
              `Report generated with ${workbook.SheetNames.length} sheet(s) for ${selectedCount} report type(s)!`
            );
          } catch (error) {
            console.error("Error generating Excel file:", error);
            message.error("Failed to generate Excel file");
          }
        },
        onFailure: (error) => {
          setExportLoading(false);
          message.error(error || "Failed to generate report");
          console.error("Export error:", error);
        },
      })
    );
  };

  // New function to handle multiple report downloads
  const downloadMultipleReports = (response) => {
    const reportsToDownload = [];
    const date = new Date().toISOString().slice(0, 10);

    // Helper function to create CSV
    const createCSV = (headers, rows) => {
      const escapeCSV = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val);
        if (/[",\n]/.test(str)) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [
        headers.join(","),
        ...rows.map((row) => headers.map((h) => escapeCSV(row[h])).join(",")),
      ].join("\n");
    };

    // Prepare Program report
    if (selectedItems.Program && response?.result?.program) {
      const programData = response.result.program;
      if (Array.isArray(programData) && programData.length > 0) {
        const headers = [
          "id",
          "name",
          "discount_type",
          "discount_value",
          "duration",
          "eligibility_rules",
          "max_redemptions",
          "approval_threshold",
          "status",
          "createdAt",
          "user_name",
        ];

        const rows = programData.map((program) => ({
          id: program.id,
          name: program.name,
          discount_type: program.discount_type === 1 ? "Fixed" : "Percentage",
          discount_value: program.discount_value,
          duration: program.duration,
          eligibility_rules: Array.isArray(program.eligibility_rules)
            ? program.eligibility_rules.join(", ")
            : "",
          max_redemptions: program.max_redemptions,
          approval_threshold: program.approval_threshold,
          status: program.status,
          createdAt: program.createdAt,
          user_name: program.user?.name || "",
        }));

        reportsToDownload.push({
          name: `programs_export_${date}.csv`,
          content: createCSV(headers, rows),
        });
      }
    }

    // Prepare Code report
    if (selectedItems.Code && response?.result?.code) {
      const codeData = response.result.code;
      if (Array.isArray(codeData) && codeData.length > 0) {
        const headers = [
          "id",
          "channel",
          "createdAt",
          "expires_at",
          "name",
          "number_of_code",
          "program",
          "start_date",
          "uses_per_code",
          "status",
          "user",
        ];

        const rows = codeData.map((r) => ({
          id: r.id,
          channel: r.channel,
          createdAt: r.createdAt,
          expires_at: r.expires_at || "",
          name: r.name || "",
          number_of_code: r.number_of_code || "",
          program: r.program?.name || "",
          start_date: r.start_date || "",
          uses_per_code: r.uses_per_code || "",
          status: r.status || "",
          user: r.user?.name || "",
        }));

        reportsToDownload.push({
          name: `codes_export_${date}.csv`,
          content: createCSV(headers, rows),
        });
      }
    }

    // Prepare Analytics report
    if (selectedItems.Analytics && response?.result?.analytic) {
      const analyticData = response.result.analytic;

      // Analytics summary
      const summaryHeaders = ["Metric", "Value"];
      const summaryRows = [
        {
          Metric: "Revenue Protected",
          Value: analyticData.topDashbordData?.revenue_protected || 0,
        },
        { Metric: "MRR", Value: analyticData.topDashbordData?.mrr || 0 },
        {
          Metric: "Active Programs",
          Value: analyticData.topDashbordData?.activeProgram || 0,
        },
        {
          Metric: "Total Redemptions",
          Value: analyticData.topDashbordData?.total_redemptions || 0,
        },
        {
          Metric: "Conversion Lift",
          Value: `${analyticData.topDashbordData?.conversion_lift || 0}%`,
        },
      ];

      reportsToDownload.push({
        name: `analytics_summary_export_${date}.csv`,
        content: createCSV(summaryHeaders, summaryRows),
      });

      // Revenue chart data if available
      if (analyticData.revenueChart) {
        const revenueHeaders = ["Month", "Revenue"];
        const revenueRows = analyticData.revenueChart.xAxis.map(
          (month, index) => ({
            Month: month,
            Revenue: analyticData.revenueChart.series[index] || 0,
          })
        );

        reportsToDownload.push({
          name: `analytics_revenue_export_${date}.csv`,
          content: createCSV(revenueHeaders, revenueRows),
        });
      }
    }

    // Prepare Applications report
    if (selectedItems.Applications && response?.result?.application) {
      const applicationData = response.result.application;
      if (
        applicationData.rows &&
        Array.isArray(applicationData.rows) &&
        applicationData.rows.length > 0
      ) {
        const headers = [
          "id",
          "name",
          "program_name",
          "discount_value",
          "channel",
          "status",
          "total_codes",
          "used_codes",
          "mrr_impact",
          "start_date",
          "expires_at",
          "user_name",
        ];

        const rows = applicationData.rows.map((app) => ({
          id: app.id,
          name: app.name,
          program_name: app.program?.name || "",
          discount_value: app.program?.discount_value || "",
          channel: app.channel,
          status: app.status,
          total_codes: app.total_codes || 0,
          used_codes: app.used_codes || 0,
          mrr_impact: app.mrr_impact || 0,
          start_date: app.start_date || "",
          expires_at: app.expires_at || "",
          user_name: app.user?.name || "",
        }));

        reportsToDownload.push({
          name: `applications_export_${date}.csv`,
          content: createCSV(headers, rows),
        });
      }
    }

    // Download all reports sequentially
    if (reportsToDownload.length === 0) {
      message.warning("No data available for the selected report types");
      return;
    }

    // Download the first report immediately
    downloadReport(reportsToDownload[0]);

    // If there are more reports, show a message and download them with a delay
    if (reportsToDownload.length > 1) {
      message.info(
        `Downloading ${reportsToDownload.length} reports... First file: ${reportsToDownload[0].name}`
      );

      // Download remaining reports with a delay to avoid browser blocking
      reportsToDownload.slice(1).forEach((report, index) => {
        setTimeout(() => {
          downloadReport(report);
        }, (index + 1) * 1000); // 1 second delay between downloads
      });
    }
  };

  // Helper function to download a single report
  const downloadReport = (report) => {
    const blob = new Blob([report.content], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = report.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Fixed Revenue Chart (Revenue trends and projection)
  const revenueSeries = useMemo(() => {
    if (!analytics?.revenueChart?.series) return [];

    try {
      // Use the series array directly from API
      const data = Array.isArray(analytics.revenueChart.series)
        ? analytics.revenueChart.series
        : [];

      return [
        {
          name: "Revenue",
          data: data.map((val) => (typeof val === "number" ? val : 0)),
        },
      ];
    } catch (error) {
      console.error("Error parsing revenue chart data:", error);
      return [];
    }
  }, [analytics]);

  const revenueOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
      },
      stroke: {
        curve: "smooth",
        width: 2,
        colors: ["#4285F4"],
      },
      grid: { borderColor: "#f1f1f1" },
      xaxis: {
        categories: analytics?.revenueChart?.xAxis
          ? analytics.revenueChart.xAxis
          : [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
        labels: {
          style: {
            colors: "#000",
            fontSize: "12px",
            fontFamily: "Rubik",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: analytics?.revenueChart?.yAxis?.min ?? 0,
        max: analytics?.revenueChart?.yAxis?.max ?? undefined,
        tickAmount: analytics?.revenueChart?.yAxis?.tickInterval
          ? Math.ceil(
              (analytics.revenueChart.yAxis.max -
                analytics.revenueChart.yAxis.min) /
                analytics.revenueChart.yAxis.tickInterval
            )
          : 6,
        labels: {
          style: {
            colors: "#000",
            fontSize: "12px",
            fontFamily: "Rubik",
          },
          formatter: (value) => {
            return value.toFixed(0);
          },
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (value) => {
            return value ? `$${value.toFixed(2)}` : "$0";
          },
        },
      },
    }),
    [analytics]
  );

  // ✅ Fixed Channel Chart (Tenant health overview)
  const channelSeries = useMemo(() => {
    if (!analytics?.channelChart?.series?.data) return [];

    try {
      // Use the data array directly from API
      const data = Array.isArray(analytics.channelChart.series.data)
        ? analytics.channelChart.series.data
        : [];

      return [
        {
          name: "Channel Usage",
          data: data.map((val) => (typeof val === "number" ? val : 0)),
        },
      ];
    } catch (error) {
      console.error("Error parsing channel chart data:", error);
      return [];
    }
  }, [analytics]);

  const channelOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: 400,
        toolbar: { show: false },
        zoom: { enabled: false },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          endingShape: "rounded",
        },
      },
      dataLabels: { enabled: false },
      stroke: { show: false },
      xaxis: {
        categories: analytics?.channelChart?.xAxis?.label
          ? analytics.channelChart.xAxis.label
          : ["Web", "Mobile", "Desktop"],
        labels: {
          style: {
            colors: "#000",
            fontSize: "12px",
            fontFamily: "Rubik",
          },
        },
      },
      yaxis: {
        min: analytics?.channelChart?.yAxis?.min ?? 0,
        max: analytics?.channelChart?.yAxis?.max ?? undefined,
        tickAmount: analytics?.channelChart?.yAxis?.tickInterval
          ? Math.ceil(
              (analytics.channelChart.yAxis.max -
                analytics.channelChart.yAxis.min) /
                analytics.channelChart.yAxis.tickInterval
            )
          : 6,
        labels: {
          style: {
            colors: "#000",
            fontSize: "12px",
            fontFamily: "Rubik",
          },
        },
      },
      legend: { show: false },
      fill: { opacity: 1 },
      colors: ["#4271D5"],
      tooltip: {
        y: {
          formatter: (val) => `${val} uses`,
        },
      },
      states: {
        normal: { filter: { type: "none" } },
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    }),
    [analytics]
  );

  return (
    <div className="">
      <div className="grid grid-cols-1 custom-grid-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">Active Programs</p>
          <p className="text-blue-600 text-xl font-bold mb-0">
            {analytics?.topDashbordData?.activeProgram || 0}
          </p>
        </div>
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">Total Redemptions</p>
          <p className="text-blue-600 text-xl font-bold mb-0">
            {analytics?.topDashbordData?.total_redemptions || 0}
          </p>
        </div>
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">MRR Impact</p>
          <p className="text-[#EA4335] text-xl font-bold mb-0">
            ${analytics?.topDashbordData?.mrr || 0}
          </p>
        </div>
        <div className="chart-card !shadow-none">
          <p className="mb-2 text-sm">Conversion lift</p>
          <p className="text-[#52C41A] text-xl font-bold mb-0">
            {analytics?.topDashbordData?.conversion_lift.toFixed(2) || 0}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6flex flex-wrap gap-5 mt-6">
        <div className="chart-card w-full">
          <h2 className="text-span text-black !font-semibold mb-0">
            Revenue trends and projection{" "}
          </h2>
          <div className="w-full">
            {revenueSeries.length > 0 && revenueSeries[0].data.length > 0 ? (
              <Chart
                options={revenueOptions}
                series={revenueSeries}
                type="line"
                height={400}
                style={{ width: "100%" }}
              />
            ) : (
              <div className="h-400 flex items-center justify-center text-gray-500">
                No revenue data available
              </div>
            )}
          </div>
        </div>

        <div className="chart-card w-full">
          <h2 className="text-span text-black !font-semibold mb-0">
            Tenant health overview{" "}
          </h2>
          <div className="w-full">
            {channelSeries.length > 0 && channelSeries[0].data.length > 0 ? (
              <Chart
                options={channelOptions}
                series={channelSeries}
                type="bar"
                height={400}
                style={{ width: "100%" }}
              />
            ) : (
              <div className="h-400 flex items-center justify-center text-gray-500">
                No channel data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Abuse Detection Alerts and Export Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Abuse Detection Alerts Section */}
        <div className="chart-card">
          <h2 className="text-span text-black !font-semibold mb-4">
            Abuse Detection Alerts
          </h2>
          <div className="">
            <div className="flex items-start gap-6 p-3 pb-7 rounded-r-lg relative">
              <div className="w-3 h-3 bg-[#114D56] rounded-full mt-1 flex-shrink-0 flex justify-center items-center">
                <div className="absolute top-[20px] bottom-0 left-[17px] w-[1px] h-full flex-1 rounded-full bg-[#114D56]"></div>
              </div>
              <div className="flex-1">
                <span className="font-semibold text-black text-sm mb-1 block">
                  Velocity Alert
                </span>
                <p className="text-gray-600 text-sm mb-1">
                  Code SAVE20 used 50+ times in 1 hour
                </p>
                <span className="text-gray-400 text-xs">2 hours ago</span>
              </div>
            </div>

            <div className="flex items-start gap-6 p-3 rounded-r-lg relative">
              <div className="w-3 h-3 bg-[#114D56] rounded-full mt-1 flex-shrink-0 flex justify-center items-center">
                <div className="absolute top-[20px] bottom-0 left-[17px] w-[1px] h-full flex-1 rounded-full bg-[#114D56]"></div>
              </div>
              <div className="flex-1">
                <span className="font-semibold text-black text-sm mb-1 block">
                  Region Violation
                </span>
                <p className="text-gray-600 text-sm mb-1">
                  AU-locked code used from US IP
                </p>
                <span className="text-gray-400 text-xs">6 hours ago</span>
              </div>
            </div>

            <div className="flex items-start gap-6 p-3 rounded-r-lg">
              <div className="w-3 h-3 bg-[#114D56] rounded-full mt-1 flex-shrink-0"></div>
              <div className="flex-1">
                <span className="font-semibold text-black text-sm mb-1 block">
                  Resolved
                </span>
                <p className="text-gray-600 text-sm mb-1">
                  Duplicate email redemption blocked
                </p>
                <span className="text-gray-400 text-xs">1 hour ago</span>
              </div>
            </div>
          </div>
        </div>

        {/* Export Reports Section */}
        <div className="chart-card">
          <div className="">
            <h2 className=" flex items-center justify-between mb-4 text-span text-black !font-semibold mb-0">
              Export Reports
              <button
                className="btn-light-blue"
                onClick={handleGenerateReport}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                )}
                {exportLoading ? "Generating..." : "Generate Report"}
              </button>
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Period
              </label>
              <RangePicker
                onChange={(dates) => setSelectedDate(dates)}
                disabledDate={(current) => current && current > new Date()}
                className="w-full border rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Types
              </label>
              <div className="grid gap-3 gap-y-5 md:grid-cols-2 mt-4">
                {items.map((item, index) => (
                  <label
                    key={index}
                    className="flex items-center gap-3 cursor-pointer select-none"
                  >
                    <div className="relative !h-5 !w-5">
                      <input
                        type="checkbox"
                        checked={selectedItems[item]}
                        onChange={() => handleItemChange(item)}
                        className="peer hidden !p-0"
                      />
                      <span className="flex h-5 w-5 items-center justify-center rounded-[16px] border-2 border-[#9BA2AB] peer-checked:border-[#4285F4] bg-white peer-checked:bg-white"></span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="hidden h-4 w-4 text-[#4285F4] peer-checked:block absolute top-[90%] left-[90%] -translate-x-1/2 -translate-y-1/2"
                        fill="#4285F4"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="text-black text-span !leading-[15px] !tracking-[0]">
                      {item}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Format
              </label>
              <div className="flex space-x-4 items-center mt-4 flex-wrap">
                {billingCycles.map((cycle) => (
                  <label
                    key={cycle.value}
                    className="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="billing-cycle"
                      value={cycle.value}
                      checked={selectedCycle === cycle.value}
                      onChange={handleCycleChange}
                      className="hidden"
                    />
                    <span
                      className={`w-5 h-5 rounded-full border-2 ${
                        selectedCycle === cycle.value
                          ? "bg-blue-500 border-blue-500"
                          : "bg-white border-gray-300"
                      } flex items-center justify-center`}
                    >
                      {selectedCycle === cycle.value && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-blue-500"
                          fill="#fff"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </span>
                    <div className="ml-2 text-black text-span">
                      {cycle.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTabs;
